import { z } from 'zod';
import { CdsFindingSchema } from './cds.js';
import { AppointmentSchema, SlotSchema } from './types/scheduling.js';
import { PatientBannerSchema } from './types/patient.js';
import { ProvenanceSchema } from './types/common.js';

/**
 * Surfaces are the only visual vocabulary of the staff app and portal. The backend decides which
 * surfaces to return for a conversational turn; the frontend renders them. There are no routes or menus.
 */
const Cell = z.union([z.string(), z.number(), z.boolean(), z.null()]);

export const TextSurface = z.object({
  type: z.literal('text'),
  markdown: z.string(),
  /** AI narrative must be labelled as such; verified data is 'system'. */
  provenance: ProvenanceSchema.default('system'),
});

export const TableSurface = z.object({
  type: z.literal('table'),
  title: z.string().optional(),
  columns: z.array(z.object({ key: z.string(), label: z.string(), align: z.enum(['left', 'right', 'center']).optional() })),
  rows: z.array(z.record(z.string(), Cell)),
  emptyMessage: z.string().optional(),
  sourceRefs: z.array(z.string()).optional().describe('Record ids the rows came from'),
});

export const ChartSurface = z.object({
  type: z.literal('chart'),
  title: z.string(),
  kind: z.enum(['line', 'bar']),
  unit: z.string().optional(),
  series: z.array(z.object({
    name: z.string(),
    points: z.array(z.object({ x: z.string(), y: z.number(), flag: z.string().optional(), id: z.string().optional() })),
  })),
  referenceRange: z.object({ low: z.number().optional(), high: z.number().optional() }).optional(),
  caption: z.string().optional(),
});

export const PatientSummarySurface = z.object({
  type: z.literal('patient-summary'),
  patient: PatientBannerSchema,
  age: z.number().int().optional(),
  sections: z.array(z.object({
    title: z.string(),
    items: z.array(z.object({ label: z.string(), value: z.string(), date: z.string().optional(), flag: z.string().optional(), id: z.string().optional(), provenance: ProvenanceSchema.optional() })),
    emptyMessage: z.string().optional(),
  })),
  narrative: z.string().optional().describe('AI-generated narrative, always labelled as such'),
});

export const CalendarSurface = z.object({
  type: z.literal('calendar'),
  title: z.string(),
  date: z.string(),
  timeZone: z.string(),
  appointments: z.array(AppointmentSchema.pick({ appointmentId: true, patientId: true, providerId: true, start: true, end: true, status: true, appointmentTypeCode: true, reason: true }).extend({ patientName: z.string().optional(), providerName: z.string().optional() })),
});

export const SlotPickerSurface = z.object({
  type: z.literal('slot-picker'),
  title: z.string(),
  slots: z.array(SlotSchema),
  /** Saying "the second one" or clicking selects a slot and triggers createAppointmentDraft. */
  selectionPrompt: z.string().optional(),
});

export const MedicationListSurface = z.object({
  type: z.literal('medication-list'),
  title: z.string().optional(),
  medications: z.array(z.object({ id: z.string(), name: z.string(), dose: z.string().optional(), route: z.string().optional(), frequency: z.string().optional(), status: z.string(), startDate: z.string().optional(), provenance: ProvenanceSchema })),
});

export const ClinicalNoteSurface = z.object({
  type: z.literal('clinical-note'),
  noteId: z.string().optional(),
  title: z.string().optional(),
  body: z.string(),
  status: z.string(),
  author: z.string().optional(),
  date: z.string().optional(),
  editable: z.boolean().default(false),
});

export const ConfirmationCardSurface = z.object({
  type: z.literal('confirmation-card'),
  pendingActionId: z.string(),
  kind: z.string(),
  title: z.string(),
  patient: PatientBannerSchema.nullable(),
  lines: z.array(z.object({ label: z.string(), value: z.string() })),
  cdsFindings: z.array(CdsFindingSchema).default([]),
  requiresAcknowledgement: z.array(z.string()).default([]),
  expiresAt: z.string(),
  confirmLabel: z.string().default('Confirm'),
  rejectLabel: z.string().default('Cancel'),
});

export const CdsAlertsSurface = z.object({
  type: z.literal('cds-alerts'),
  findings: z.array(CdsFindingSchema),
  engine: z.string(),
});

export const FormFieldSchema = z.object({
  name: z.string(),
  label: z.string(),
  kind: z.enum(['text', 'date', 'select', 'phone', 'email', 'number', 'textarea', 'checkbox']),
  required: z.boolean().default(false),
  options: z.array(z.object({ value: z.string(), label: z.string() })).optional(),
  value: z.string().optional(),
  placeholder: z.string().optional(),
});

export const FormSurface = z.object({
  type: z.literal('form'),
  formId: z.string().describe('Submitting posts { formId, values } back to the chat endpoint'),
  title: z.string(),
  fields: z.array(FormFieldSchema),
  submitLabel: z.string().default('Continue'),
  /** Fields the conversation still needs; rendered highlighted. */
  missing: z.array(z.string()).default([]),
});

export const PatientIndicatorSurface = z.object({
  type: z.literal('patient-indicator'),
  patient: PatientBannerSchema.nullable(),
  activeEncounterId: z.string().nullable(),
});

export const ClarificationSurface = z.object({
  type: z.literal('clarification'),
  question: z.string(),
  /** Distinguishes where the uncertainty comes from so the UI can word it honestly. */
  reason: z.enum(['TRANSCRIPTION', 'INTERPRETATION', 'AMBIGUOUS_MATCH', 'MISSING_PARAMETER', 'VALIDATION', 'CONFIRMATION']),
  options: z.array(z.object({ id: z.string(), label: z.string(), detail: z.string().optional(), reply: z.string().describe('Text sent as the user message if chosen') })).default([]),
});

export const ErrorSurface = z.object({
  type: z.literal('error'),
  code: z.string(),
  message: z.string(),
  recoverable: z.boolean().default(true),
});

export const SurfaceSchema = z.discriminatedUnion('type', [
  TextSurface, TableSurface, ChartSurface, PatientSummarySurface, CalendarSurface, SlotPickerSurface,
  MedicationListSurface, ClinicalNoteSurface, ConfirmationCardSurface, CdsAlertsSurface, FormSurface,
  PatientIndicatorSurface, ClarificationSurface, ErrorSurface,
]);
export type Surface = z.infer<typeof SurfaceSchema>;
export type SurfaceType = Surface['type'];
